import { Place, Trip } from "./interfaces";
import { isIdea, TSDate } from "./time";

/**
 * Where a trip sits relative to today.
 *
 * - `undated`      — nothing in the trip carries a real date (all ideas, or a legacy record whose
 *                    only dates are 1900 sentinels). Never treated as past: we can't know.
 * - `upcoming`     — the trip hasn't started yet.
 * - `active`       — today falls inside the trip.
 * - `just-finished`— ended within JUST_FINISHED_GRACE_DAYS. Still fully "live": you're adding
 *                    photos, notes and receipts. Chrome stays normal, but forward-looking warnings
 *                    (missing lodging, unfinished drafts) are pointless and get suppressed.
 * - `past`         — ended longer ago than the grace period. Gets differentiated chrome so you
 *                    don't mistake last year's Vienna trip for the one you're planning.
 */
export type TripStatus = 'undated' | 'upcoming' | 'active' | 'just-finished' | 'past';

/**
 * How long after a trip ends it still behaves like a live trip. The day you land you're still
 * editing, so nagging about lodging you already slept in — or greying out the header — is noise.
 */
export const JUST_FINISHED_GRACE_DAYS = 7;

export interface TripDateRange {
    start: TSDate;
    end: TSDate;
}

/**
 * The real date span of a trip, derived from its dated activities and falling back to the trip's
 * own start date.
 *
 * Every date is filtered through `isIdea()`. That is load-bearing, not defensive: the Firestore
 * converter maps a missing `startdatetime` to IDEA_TSDATE (1900-01-01), and legacy records persist
 * that sentinel directly. An unguarded min() over these dates yields a range ~126 years wide, which
 * has already caused one out-of-memory crash (the calendar built a day cell per day).
 *
 * Returns undefined when the trip has no real dates at all.
 */
export function tripDateRange(trip: Trip, activities: Place[]): TripDateRange | undefined {
    let start: TSDate | undefined;
    let end: TSDate | undefined;

    const consider = (date: TSDate | null | undefined) => {
        if (isIdea(date)) {
            return; // null, undefined, or a pre-1990 sentinel — not a real date
        }
        if (!start || date.isBefore(start)) {
            start = date;
        }
        if (!end || date.isAfter(end)) {
            end = date;
        }
    };

    consider(trip.startdate);
    for (const activity of activities) {
        consider(activity.startdatetime);
        consider(activity.enddatetime);
    }

    return start && end ? { start, end } : undefined;
}

/**
 * Classifies a trip relative to `today` (defaulting to the actual today; injectable for tests).
 */
export function tripStatus(trip: Trip, activities: Place[], today: TSDate = TSDate.today()): TripStatus {
    const range = tripDateRange(trip, activities);
    if (!range) {
        return 'undated';
    }
    if (today.isBefore(range.start)) {
        return 'upcoming';
    }
    if (today.isEqualOrBefore(range.end)) {
        return 'active';
    }
    return today.isEqualOrBefore(range.end.plus({ days: JUST_FINISHED_GRACE_DAYS }))
        ? 'just-finished'
        : 'past';
}

/** True for trips that get differentiated (grey, badged) chrome. */
export function isPastTrip(status: TripStatus): boolean {
    return status === 'past';
}

/**
 * True once a trip is over — including the grace period. Forward-looking warnings (missing lodging,
 * unfinished drafts) are noise here: there is nothing left to plan.
 */
export function isTripOver(status: TripStatus): boolean {
    return status === 'just-finished' || status === 'past';
}
